import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable()
export class FornecedorService {
  private urlModulo = `${environment.urlAPI}/fornecedor`;

  constructor(private http: HttpClient) {}

  /**
   * Recupera a lista de fornecedores
   * @returns Promise com a lista de fornecedores
   */
  public getListaFornecedores(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-lista`)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedores);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os sistemas de gestão de um fornecedor
   * @param codFornecedor Código do fornecedor
   * @returns Promise com os sistemas de gestão do fornecedor
   */
  public getSistemaGestaoFornecedor(codFornecedor: number): Promise<any> {
    const params = new HttpParams().set('codFornecedor', codFornecedor.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-sgforn`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedores);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os dados de um fornecedor por ID
   * @param codFornecedor Código do fornecedor
   * @returns Promise com os dados do fornecedor
   */
  public getFornecedorById(codFornecedor: number): Promise<any> {
    const params = new HttpParams().set('uid', codFornecedor.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-byid`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedor);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os dados resumidos de um fornecedor por ID
   * @param codFornecedor Código do fornecedor
   * @returns Promise com os dados resumidos do fornecedor
   */
  public getFornecedorByIdResumido(codFornecedor: number): Promise<any> {
    const params = new HttpParams().set('uid', codFornecedor.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-byidResumido`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedor);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os fornecedores por status
   * @param status Status do fornecedor
   * @returns Promise com a lista de fornecedores
   */
  public getFornecedorByStatus(status: number): Promise<any> {
    const params = new HttpParams().set('status', status.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-bystatus`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedores);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera um fornecedor pelo código
   * @param codFornecedor Código do fornecedor
   * @returns Promise com os dados do fornecedor
   */
  public getFornecedorByCodFornecedor(codFornecedor: number): Promise<any> {
    const params = new HttpParams().set('codFornecedor', codFornecedor.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-bycodforn`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.fornecedor);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera o arquivo de certificado do fornecedor
   * @param codigo Código do fornecedor
   * @returns Promise com os dados do arquivo de certificado
   */
  public getArquivoCertificadoFornecedor(codigo: number): Promise<any> {
    const params = new HttpParams().set('codigo', codigo.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-arquivoCertificado`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.arquivo);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os dados do grupo de desenvolvimento para o Sistema de Gestão
   * @param codigo Código do fornecedor
   * @returns Promise com os dados do grupo
   */
  public getGrupoSistemaGestaoFornecedor(codigo: number): Promise<any> {
    const params = new HttpParams().set('codigo', codigo.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-grupoSistemaGestao`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.grupo);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva a candidatura do fornecedor
   * @param obj Dados do fornecedor para gravar no banco
   * @returns Promise com o resultado da operação
   */
  public salvaFornecedorCandidatura(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvarCandidatura`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva dados do fornecedor
   * @param campos Campos a serem atualizados
   * @param codUsuario Código do usuário
   * @returns Promise com o resultado da operação
   */
  public salvaFornecedor(campos: any, codUsuario: any): Promise<any> {
    const payload = {
      campos: campos,
      codUsuario: codUsuario
    };

    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvar`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva a nota do fornecedor
   * @param obj Dados da nota para gravar no banco
   * @returns Promise com o resultado da operação
   */
  public salvaNotaFornecedor(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvarnota`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera as notas do fornecedor
   * @param codFornecedor Código do fornecedor
   * @param codSistemaGestao Código do sistema de gestão
   * @returns Promise com as notas do fornecedor
   */
  public getNotasFornecedor(codFornecedor: number, codSistemaGestao: number): Promise<any> {
    const params = new HttpParams()
      .set('codFornecedor', codFornecedor.toString())
      .set('codSistemaGestao', codSistemaGestao.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-getnotas`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.notas);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera as notas do fornecedor por UID e sigla
   * @param uidFornecedor UID do fornecedor
   * @param siglaSistemaGestao Sigla do sistema de gestão
   * @returns Promise com as notas do fornecedor
   */
  public getNotasFornecedorByUidSigla(uidFornecedor: number, siglaSistemaGestao: string): Promise<any> {
    const params = new HttpParams()
      .set('uid', uidFornecedor.toString())
      .set('siglaSistemaGestao', siglaSistemaGestao);
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-getnotasbyuidsigla`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.notas);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os dados do workflow do fornecedor
   * @param codWorkflow Código do workflow
   * @returns Promise com os dados do workflow
   */
  public getFornecedorDadosWorkflow(codWorkflow: number): Promise<any> {
    const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-dadosWorkflow`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.dados);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera os dados do workflow do fornecedor por estação
   * @param codWorkflow Código do workflow
   * @param tipoEstacao Tipo da estação
   * @returns Promise com os dados do workflow
   */
  public getFornecedorDadosWorkflowByEstacao(codWorkflow: number, tipoEstacao: number): Promise<any> {
    const params = new HttpParams()
      .set('codWorkflow', codWorkflow.toString())
      .set('tipoEstacao', tipoEstacao.toString());
    
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlModulo}-dadosWorkflowByEstacao`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.dados);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Deleta o arquivo de certificado do fornecedor
   * @param codigo Código do arquivo
   * @param limparRegistroFornecedor Indica se deve limpar o registro do fornecedor
   * @returns Promise com o resultado da operação
   */
  public deletaArquivoCertificado(codigo: number, limparRegistroFornecedor: boolean): Promise<any> {
    const params = new HttpParams()
      .set('codigo', codigo.toString())
      .set('limparRegistroFornecedor', limparRegistroFornecedor.toString());
    
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.urlModulo}-arquivoCertificado`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva o link do certificado no banco
   * @param obj Dados do certificado
   * @returns Promise com o resultado da operação
   */
  public salvaCertificado(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvarcertificado`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva a mantenedora do usuário
   * @param obj Dados da mantenedora
   * @returns Promise com o resultado da operação
   */
  public salvaMantenedora(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvarmantenedora`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva dados do grupo
   * @param obj Dados do grupo
   * @returns Promise com o resultado da operação
   */
  public salvaGrupo(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvargrupo`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Salva o link da logo do fornecedor
   * @param obj Dados da logo
   * @returns Promise com o resultado da operação
   */
  public salvaLogo(obj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-salvarlogo`, obj)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Altera o status de um fornecedor
   * @param codigo Código do fornecedor
   * @param status Novo status
   * @returns Promise com o resultado da operação
   */
  public alterarStatusFornecedor(codigo: number, status: number): Promise<any> {
    const payload = {
      codigo: codigo,
      status: status
    };
    
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-alterarstatus`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Altera o status de um fornecedor por código de usuário
   * @param codUsuario Código do usuário
   * @param status Novo status
   * @returns Promise com o resultado da operação
   */
  public alterarStatusFornecedorByCodUsuario(payload): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlModulo}-alterarstatus-byuid`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/fornecedor.service.ts